import { CACHE_KEYS, CACHE_OPTIONS } from "../constants/cache-keys";
import { Account, AuthState, IErrorResponse } from "../interfaces/all";
import { CacheManager } from "../utils/cache-manager";
import { authLogin, authLogout, authRefreshToken, getAccount } from "./api";

class AuthService {
  private static instance: AuthService;
  private showErrorCallback: ((error: unknown) => void) | null = null;

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  public setErrorCallback(callback: (error: unknown) => void) {
    this.showErrorCallback = callback;
  }

  public async initialize(): Promise<Account> {
    try {
      const response = await getAccount();

      if (response.status !== "success") {
        throw new Error(response.message || "Erro ao buscar dados do usuário");
      }

      const { user, company } = response.data;

      if (!user || !company) {
        throw new Error("Erro ao buscar dados do usuário ou da empresa");
      }

      const account: Account = {
        ...user,
        company: company,
      };

      return account;
    } catch (error) {
      // Se for um erro de autenticação, apenas propaga
      if (
        error &&
        typeof error === "object" &&
        "type" in error &&
        (error as { type: string }).type === "authentication"
      ) {
        throw error;
      }
      // Para outros tipos de erro, mostra a mensagem
      this.showError(error);
      throw error;
    }
  }

  public async login(identifier: string, password: string): Promise<Account> {
    try {
      const response = await authLogin(identifier, password);

      if (response.status !== "success") {
        throw new Error(response.message || "Erro ao fazer login");
      }

      const { user } = response.data;

      if (!user) {
        throw new Error("Erro ao buscar dados do usuário");
      }

      if (response.data.token) {
        localStorage.setItem("wpp_access_token", response.data.token);
      }

      if (user.company_id) {
        localStorage.setItem("wpp_company_id", user.company_id);
      }

      this.setAuthState("login");

      // Busca os dados completos da conta
      const accountResponse = await getAccount();
      if (
        accountResponse.status !== "success" ||
        !accountResponse.data.user ||
        !accountResponse.data.company
      ) {
        throw new Error("Erro ao buscar dados da conta");
      }

      const account: Account = {
        ...accountResponse.data.user,
        company: accountResponse.data.company,
      };

      return account;
    } catch (error) {
      this.setAuthState("logout");
      // Se for um erro de autenticação, apenas propaga
      if (
        error &&
        typeof error === "object" &&
        "type" in error &&
        (error as { type: string }).type === "authentication"
      ) {
        throw error;
      }
      // Para outros tipos de erro, mostra a mensagem
      this.showError(error);
      throw error;
    }
  }

  public async logout(): Promise<void> {
    try {
      await authLogout();
    } catch (error) {
      console.error("Erro no logout remoto", error);
    }
    this.setAuthState("logout");
    localStorage.removeItem("wpp_access_token");
    localStorage.removeItem("wpp_company_id");
  }

  public async refreshToken(): Promise<void> {
    try {
      const response = await authRefreshToken();

      if (response.status !== "success") {
        throw new Error(response.message || "Erro ao atualizar token");
      }
    } catch (error) {
      // Se for um erro de autenticação, apenas propaga
      if (
        error &&
        typeof error === "object" &&
        "type" in error &&
        (error as { type: string }).type === "authentication"
      ) {
        throw error;
      }
      // Para outros tipos de erro, mostra a mensagem
      this.showError(error);
      throw error;
    }
  }

  private setAuthState(state: AuthState) {
    CacheManager.set(CACHE_KEYS.AUTH.STATE, state, CACHE_OPTIONS.AUTH.STATE);
  }

  public getAuthState(): AuthState {
    return (
      CacheManager.get<AuthState>(
        CACHE_KEYS.AUTH.STATE,
        CACHE_OPTIONS.AUTH.STATE,
      ) || "logout"
    );
  }

  private showError(error: unknown) {
    if (this.showErrorCallback) {
      this.showErrorCallback(error);
    }
  }

  public showValidationError(message: string) {
    const errorResponse: IErrorResponse = {
      status: "error",
      type: "validation",
      code: "AUTH-VAL-001",
      message: message,
      userMessage: message,
    };

    this.showError(errorResponse);
  }
}

export type { AuthState };
export default AuthService.getInstance();
